import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '$lib/components/ui/Button';
import { StatusPill } from '$lib/components/ui/StatusPill';
import { SkeletonTableRow } from '$lib/components/ui/Skeleton';
import { uiRunStatus } from '$lib/api/adminScraper';
import type { ScrapeRunSummary, PipelineHealthPortal } from '$lib/api/adminScraper';
import { formatDateLong, formatNumber } from '$lib/lib/formatters';
import { cn } from '$lib/lib/cn';

export interface RecentRunsTableProps {
  runs: ScrapeRunSummary[];
  // Used to map portal_slug → display name without re-fetching /portals.
  portals: PipelineHealthPortal[];
  loading: boolean;
  windowDays: 5 | 30;
  onWindowChange: (days: 5 | 30) => void;
}

// Saved/Found per stage_progress JSONB. Backend's summary doesn't expose
// these counters — they live on the detail endpoint. The table shows them
// only for completed runs by reading `target_count` for "Found" and
// inferring "Saved" as `target_count - error_count` (a reasonable proxy
// until DEV-216 surfaces stage_progress on the summary).
function foundFromRun(run: ScrapeRunSummary): number | null {
  return run.target_count;
}

function savedFromRun(run: ScrapeRunSummary): number | null {
  if (run.target_count == null) return null;
  return Math.max(0, run.target_count - run.error_count);
}

function durationCopy(run: ScrapeRunSummary): string {
  const start = run.started_at ? new Date(run.started_at).getTime() : null;
  const end = run.completed_at
    ? new Date(run.completed_at).getTime()
    : run.status === 'running'
      ? Date.now()
      : null;
  if (!start || !end) return '—';
  const ms = Math.max(0, end - start);
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function RecentRunsTable({
  runs,
  portals,
  loading,
  windowDays,
  onWindowChange
}: RecentRunsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const portalNameBySlug = Object.fromEntries(portals.map((p) => [p.slug, p.name]));

  return (
    <section className="px-4 py-3" data-testid="recent-runs">
      <header className="flex items-center gap-2 pb-2">
        <h2 className="text-sm font-semibold text-fg">{t('admin.recentRuns.title')}</h2>
        <span className="text-2xs text-fg-subtle">
          {windowDays === 5 ? t('admin.recentRuns.windowDefault') : t('admin.recentRuns.windowExpanded')}
        </span>
        <div className="ml-auto">
          <Button
            size="sm"
            variant="ghost"
            data-testid="recent-runs-window-toggle"
            onClick={() => onWindowChange(windowDays === 5 ? 30 : 5)}
          >
            {windowDays === 5 ? t('admin.action.showMore') : t('admin.action.showLess')}
          </Button>
        </div>
      </header>

      <div className="overflow-x-auto rounded border border-rule">
        <table
          className="w-full border-collapse text-xs"
          aria-label={t('admin.recentRuns.tableAriaLabel')}
          data-testid="recent-runs-table"
        >
          <thead className="bg-surface-2 text-fg-muted">
            <tr className="h-row">
              <th scope="col" className="px-3 text-left font-medium">{t('admin.recentRuns.columns.portal')}</th>
              <th scope="col" className="px-3 text-left font-medium">{t('admin.recentRuns.columns.started')}</th>
              <th scope="col" className="px-3 text-left font-medium">{t('admin.recentRuns.columns.duration')}</th>
              <th scope="col" className="px-3 text-left font-medium">{t('admin.recentRuns.columns.status')}</th>
              <th scope="col" className="px-3 text-right font-medium">{t('admin.recentRuns.columns.found')}</th>
              <th scope="col" className="px-3 text-right font-medium">{t('admin.recentRuns.columns.saved')}</th>
              <th scope="col" className="px-3 text-right font-medium">{t('admin.recentRuns.columns.errors')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && runs.length === 0
              ? Array.from({ length: 5 }, (_, i) => <SkeletonTableRow key={i} />)
              : null}

            {!loading && runs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-2xs text-fg-subtle" data-testid="recent-runs-empty">
                  {t('admin.recentRuns.empty')}
                </td>
              </tr>
            )}

            {runs.map((run) => {
              const status = uiRunStatus(run.status, run.error_count);
              const found = foundFromRun(run);
              const saved = savedFromRun(run);
              const portalName = portalNameBySlug[run.portal_slug] ?? run.portal_slug;
              return (
                <tr
                  key={run.id}
                  className={cn('h-row cursor-pointer border-t border-rule', 'hover:bg-surface-2 focus-within:bg-surface-2')}
                  onClick={() => navigate(`/admin/runs/${run.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/admin/runs/${run.id}`);
                    }
                  }}
                  tabIndex={0}
                  role="link"
                  aria-label={t('admin.recentRuns.rowAriaLabel', {
                    portal: portalName,
                    time: formatDateLong(run.started_at)
                  })}
                  data-testid={`recent-runs-row-${run.id}`}
                >
                  <td className="px-3 text-fg">{portalName}</td>
                  <td className="px-3 num text-fg-muted">{formatDateLong(run.started_at)}</td>
                  <td className="px-3 num text-fg-muted">{durationCopy(run)}</td>
                  <td className="px-3">
                    <StatusPill status={status} errorCount={run.error_count} />
                  </td>
                  <td className="px-3 num text-right text-fg-muted">{formatNumber(found)}</td>
                  <td className="px-3 num text-right text-fg-muted">{formatNumber(saved)}</td>
                  <td className="px-3 num text-right text-fg-muted">{formatNumber(run.error_count)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
